/**
 * Reseller Cart Validator
 * Removes reseller-discounted items when user is no longer a reseller.
 */
(function () {
  'use strict';

  var RESELLER_TAGS = new Set([
    'reseller',
    'reseller_eu',
    'reseller eu',
    'reseller_eu_light',
    'reseller eu light'
  ]);

  var RESELLER_DISCOUNT_CODES = ['RESELLER50', 'RESELLER58', 'RESELLEREU58'];
  var TOAST_ID = 'cleenlab-reseller-cart-toast';
  var TOAST_MESSAGE = 'Your cart has been updated. Reseller discounts are only available when logged in.';

  var STORAGE_WAS_RESELLER_KEY = 'wasReseller';
  var STORAGE_HAS_VALIDATED_KEY = 'hasValidatedCart';

  var validationInFlight = false;
  var removalInFlight = false;
  var runScheduledForPage = false;

  function normalizeTag(tag) {
    return String(tag || '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, ' ');
  }

  function normalizeCode(value) {
    return String(value || '').trim().toUpperCase();
  }

  function readSessionBool(key) {
    try {
      return sessionStorage.getItem(key) === 'true';
    } catch (error) {
      return false;
    }
  }

  function writeSessionBool(key, value) {
    try {
      sessionStorage.setItem(key, value ? 'true' : 'false');
    } catch (error) {
      // Ignore storage errors (private mode / blocked storage)
    }
  }

  function getCustomerTags() {
    if (Array.isArray(window.customerTags)) {
      return window.customerTags;
    }

    if (Array.isArray(window.CLEENLAB_CUSTOMER_TAGS)) {
      return window.CLEENLAB_CUSTOMER_TAGS;
    }

    return [];
  }

  function isReseller() {
    if (window.isResellerCustomer === true) {
      return true;
    }

    return getCustomerTags().some(function (tag) {
      return RESELLER_TAGS.has(normalizeTag(tag));
    });
  }

  function valueHasResellerDiscountCode(value) {
    var normalized = normalizeCode(value);
    if (!normalized) return false;

    return RESELLER_DISCOUNT_CODES.some(function (code) {
      return normalized.indexOf(code) !== -1;
    });
  }

  function hasResellerDiscountInProperties(properties) {
    if (!properties || typeof properties !== 'object') return false;

    var keys = Object.keys(properties);
    for (var i = 0; i < keys.length; i += 1) {
      var key = keys[i];
      var value = properties[key];

      if (valueHasResellerDiscountCode(value)) {
        return true;
      }

      if (/discount/i.test(String(key || '')) && valueHasResellerDiscountCode(value)) {
        return true;
      }
    }

    return false;
  }

  function hasResellerDiscountInAllocations(allocations) {
    if (!Array.isArray(allocations)) return false;

    return allocations.some(function (allocation) {
      if (!allocation) return false;

      if (valueHasResellerDiscountCode(allocation.title)) return true;
      if (valueHasResellerDiscountCode(allocation.code)) return true;

      var app = allocation.discount_application || {};
      if (valueHasResellerDiscountCode(app.title)) return true;
      if (valueHasResellerDiscountCode(app.code)) return true;

      return false;
    });
  }

  function hasResellerDiscount(item) {
    if (!item || typeof item !== 'object') return false;

    if (hasResellerDiscountInProperties(item.properties)) {
      return true;
    }

    if (hasResellerDiscountInAllocations(item.discounts)) {
      return true;
    }

    if (hasResellerDiscountInAllocations(item.discount_allocations)) {
      return true;
    }

    if (hasResellerDiscountInAllocations(item.line_level_discount_allocations)) {
      return true;
    }

    return false;
  }

  function showInlineToast(message) {
    var existing = document.getElementById(TOAST_ID);
    if (existing) existing.remove();

    var toast = document.createElement('div');
    toast.id = TOAST_ID;
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', 'polite');
    toast.textContent = message;
    toast.style.cssText = [
      'position:fixed',
      'right:16px',
      'bottom:16px',
      'z-index:1000002',
      'max-width:360px',
      'padding:12px 14px',
      'border-radius:12px',
      'background:#121212',
      'color:#ffffff',
      'font-size:13px',
      'line-height:1.4',
      'box-shadow:0 8px 24px rgba(0,0,0,0.25)'
    ].join(';');

    document.body.appendChild(toast);

    window.setTimeout(function () {
      if (toast && toast.parentNode) toast.parentNode.removeChild(toast);
    }, 4200);
  }

  function showNotification(message) {
    try {
      if (typeof window.showToast === 'function') {
        window.showToast(message);
        return;
      }

      showInlineToast(message);
    } catch (error) {
      console.error('Reseller validator notification failed:', error);
    }
  }

  function dispatchCartUpdatedEvents(cartData) {
    var detail = { cartData: cartData || null };

    document.dispatchEvent(new CustomEvent('cart:updated', { detail: detail }));
    document.dispatchEvent(new CustomEvent('cart-updated', { detail: detail }));
    document.dispatchEvent(new CustomEvent('cart:refresh', { detail: detail }));
  }

  function shouldReloadPageAfterCleanup() {
    return window.location.pathname.indexOf('/cart') === 0;
  }

  function removeResellerItems(items) {
    if (!Array.isArray(items) || !items.length || removalInFlight) {
      return Promise.resolve();
    }

    removalInFlight = true;

    var updates = {};
    items.forEach(function (item) {
      if (item && item.key) {
        updates[item.key] = 0;
      }
    });

    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates: updates })
    })
      .then(function (response) {
        if (!response.ok) {
          throw new Error('Failed to remove reseller items');
        }
        return response.json();
      })
      .then(function (updatedCart) {
        showNotification(TOAST_MESSAGE);
        dispatchCartUpdatedEvents(updatedCart);

        if (shouldReloadPageAfterCleanup()) {
          window.setTimeout(function () {
            window.location.reload();
          }, 450);
        }
      })
      .catch(function (error) {
        console.error('Reseller validator removal error:', error);
      })
      .finally(function () {
        removalInFlight = false;
      });
  }

  function validateResellerCart(currentResellerStatus) {
    if (validationInFlight || removalInFlight) {
      return Promise.resolve(false);
    }

    validationInFlight = true;

    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (response) {
        if (!response.ok) {
          throw new Error('Failed to load cart');
        }
        return response.json();
      })
      .then(function (cart) {
        if (currentResellerStatus) {
          return false;
        }

        var items = Array.isArray(cart && cart.items) ? cart.items : [];
        var itemsToRemove = items.filter(function (item) {
          return hasResellerDiscount(item);
        });

        if (!itemsToRemove.length) {
          writeSessionBool(STORAGE_HAS_VALIDATED_KEY, true);
          return false;
        }

        return removeResellerItems(itemsToRemove).then(function () {
          writeSessionBool(STORAGE_HAS_VALIDATED_KEY, true);
          return true;
        });
      })
      .catch(function (error) {
        console.error('Reseller cart validation error:', error);
        return false;
      })
      .finally(function () {
        validationInFlight = false;
      });
  }

  function runValidationGate(source) {
    if (runScheduledForPage) {
      return;
    }
    runScheduledForPage = true;

    var previousResellerStatus = readSessionBool(STORAGE_WAS_RESELLER_KEY);
    var hasValidatedThisSession = readSessionBool(STORAGE_HAS_VALIDATED_KEY);
    var currentResellerStatus = isReseller();

    var shouldValidate =
      (previousResellerStatus && !currentResellerStatus) ||
      (!hasValidatedThisSession && !currentResellerStatus);

    console.log('Reseller Validator:', {
      source: source,
      previousStatus: previousResellerStatus,
      currentStatus: currentResellerStatus,
      shouldValidate: shouldValidate,
      hasValidated: hasValidatedThisSession
    });

    writeSessionBool(STORAGE_WAS_RESELLER_KEY, currentResellerStatus);

    if (currentResellerStatus) {
      // Allow a fresh validation run if user logs out later in the same browser session.
      writeSessionBool(STORAGE_HAS_VALIDATED_KEY, false);
      return;
    }

    if (!shouldValidate) {
      return;
    }

    validateResellerCart(currentResellerStatus);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      runValidationGate('DOMContentLoaded');
    });
  } else {
    runValidationGate('immediate');
  }

  window.addEventListener('pageshow', function () {
    runValidationGate('pageshow');
  });
})();
